import { isDesignV2, v2OpenPanels, applyV2Panel } from '../thunk/appNavThunk';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import {
    setActiveCategory,
    setActiveTab,
    pushNavEntry,
    replaceTopNavEntry,
    setNavSkipPush,
    featureToCategoryMap,
} from '../slices/appSlice';
import {
    setArtistResults,
    setTrackResults,
    setMixResults,
    setTrackListResults,
    toggleExpandedMix,
    clearResults,
    DJPAL_SNAPSHOT_KEYS,
} from '../slices/djpalSlice';
import { setSubTab as setFeedSubTab } from '../slices/feedSlice';

// Real web (browser) uses browser URL history (pushState/popstate) — skip the
// app-level stack there. Desktop (Electron) AND native (Capacitor iOS/Android)
// both need the app-level stack: native is a WebView SPA with no browser
// back/forward UI, so its swipe-nav (App.js) drives THIS stack. NB: `isWeb` is
// true on native too (no window.electron.db), so we must exclude native here —
// otherwise the stack never builds and back/forward no-op on device.
const isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;
const isNative = typeof window !== 'undefined'
    && !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
const isRealWeb = isWeb && !isNative;

const snapshotDjpal = (djpal) => {
    const snap = {};
    for (const key of DJPAL_SNAPSHOT_KEYS) snap[key] = djpal[key];
    return snap;
};

// "Clean marker" snapshot for djpal entries pushed by tab/category navigation.
// Strips results so that the next search's setXxxResults REPLACES this entry
// (rather than stacking a duplicate when prior results were already in state).
const cleanDjpalSnapshot = (djpal) => ({
    ...snapshotDjpal(djpal),
    artistResults: null,
    trackResults: null,
    mixResults: null,
    trackListResults: null,
});

const RESULT_KIND_BY_ACTION = {
    [setArtistResults.type]: 'artist',
    [setTrackResults.type]: 'track',
    [setMixResults.type]: 'mix',
    [setTrackListResults.type]: 'trackList',
};

const RESULT_FIELD_BY_KIND = {
    artist: 'artistResults',
    track: 'trackResults',
    mix: 'mixResults',
    trackList: 'trackListResults',
};

const navHistoryMiddleware = createListenerMiddleware();

// DESIGN V2: a tab navigation (sidebar, user menu, search) leaves whatever
// collection / DJ page was open on top of the content column.
const leaveV2Panels = (api) => {
    if (!isDesignV2()) return;
    const open = v2OpenPanels(api.getState());
    if (Object.values(open).some(Boolean)) api.dispatch(applyV2Panel(null));
};

// Consume and clear navSkipPush. If the flag is set, middleware should no-op
// for exactly ONE action, then return to normal. Returning without clearing
// would leave the flag stuck because replace/push dispatches from this
// middleware are themselves suppressed by the same guard.
const consumeSkip = (app, api) => {
    if (app.navSkipPush) {
        api.dispatch(setNavSkipPush(false));
        return true;
    }
    return false;
};

navHistoryMiddleware.startListening({
    actionCreator: setActiveCategory,
    effect: (action, api) => {
        if (isRealWeb) return;
        // Native has no hub grid — a category entry (tab: null) is a
        // destination that can't render there. App.js's "web defaults to
        // Feed" effect immediately redirects tab:null → 'feed' and re-pushes,
        // so back onto such an entry played the slide animation and landed on
        // the SAME screen (the ghost-back on For You). Sources on native: the
        // boot seed + onboarding's setActiveCategory('library').
        if (isNative) return;
        const { app } = api.getState();
        if (consumeSkip(app, api)) return;
        leaveV2Panels(api);
        // Re-clicking the category whose grid is already showing is not a
        // navigation (same dedupe rationale as setActiveTab below). Only skip
        // when the grid was showing (activeTab null) — clicking the category
        // header while inside one of its tabs IS a navigation to the grid.
        // Never skip the very first entry: App.js seeds the stack at boot by
        // dispatching setActiveCategory('explore'), which matches the initial
        // state and would otherwise be swallowed.
        const orig = api.getOriginalState().app;
        if (orig.navIndex >= 0 && orig.activeTab === null && orig.activeCategory === action.payload) return;
        api.dispatch(pushNavEntry({ category: action.payload, tab: null, djpal: null }));
    },
});

navHistoryMiddleware.startListening({
    actionCreator: setActiveTab,
    effect: (action, api) => {
        if (isRealWeb) return;
        const { app, djpal, feed, automix } = api.getState();
        if (consumeSkip(app, api)) return;
        leaveV2Panels(api);
        const tab = action.payload;

        // Re-selecting the already-active tab is not a navigation. Pushing
        // would stack a duplicate entry (one dead Back press) AND trim any
        // forward branch. UI tab tiles / navbar links / deep-link helpers all
        // dispatch setActiveTab unconditionally, so dedupe here, at the single
        // choke point. Deep links into Mixpedia that fire while ALREADY on
        // Mixpedia still get their nav entry from the results action.
        if (api.getOriginalState().app.activeTab === tab) return;
        const category = featureToCategoryMap[tab] || app.activeCategory;

        // Before leaving Mixpedia, if the top djpal entry is a clean marker
        // (no results captured) but the Redux djpal state has stale results
        // from a prior session, refresh the marker so forward-nav restores
        // what the user actually saw. Don't touch real search entries.
        const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
        if (tab !== 'djpal' && top && top.tab === 'djpal' && top.djpal) {
            const topIsClean =
                top.djpal.artistResults == null &&
                top.djpal.trackResults == null &&
                top.djpal.mixResults == null &&
                top.djpal.trackListResults == null;
            if (topIsClean) {
                api.dispatch(replaceTopNavEntry({ ...top, djpal: snapshotDjpal(djpal) }));
            }
        }

        const entry = {
            category,
            tab,
            djpal: tab === 'djpal' ? cleanDjpalSnapshot(djpal) : null,
            feedSubTab: tab === 'feed' ? feed.subTab : null,
            // Automix's set view is a page inside the tab (openAutomixSet
            // pushes its own entry). Record which set is open when arriving,
            // so coming back to this stop restores the same view.
            automix: tab === 'automix' ? { setDir: automix.set?.dir || null } : null,
        };
        api.dispatch(pushNavEntry(entry));
    },
});

// Feed sub-tab (Tracks/Mixes) switches become their own nav entries so
// back/forward toggles between them.
navHistoryMiddleware.startListening({
    actionCreator: setFeedSubTab,
    effect: (action, api) => {
        if (isRealWeb) return;
        const { app } = api.getState();
        if (consumeSkip(app, api)) return;
        if (app.activeTab !== 'feed') return;
        const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
        // No-op if the top entry already matches (prevents duplicate entries on
        // re-dispatch of the same sub-tab).
        if (top && top.tab === 'feed' && top.feedSubTab === action.payload) return;
        api.dispatch(pushNavEntry({
            category: 'explore',
            tab: 'feed',
            djpal: null,
            feedSubTab: action.payload,
        }));
    },
});

const handleResultsUpdate = (action, api) => {
    if (isRealWeb) return;
    const { app, djpal } = api.getState();
    if (consumeSkip(app, api)) return;
    if (app.activeTab !== 'djpal') return;
    // The slice dropped this payload (search resolved after the user went
    // home — see suppressLateResults). No state changed, so no nav entry.
    if (djpal.suppressLateResults) return;

    const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
    const snapshot = snapshotDjpal(djpal);
    const kind = RESULT_KIND_BY_ACTION[action.type];
    const resultField = RESULT_FIELD_BY_KIND[kind];

    // Replace only when the top entry is a "clean marker" waiting for this exact
    // kind of search to complete: tab=djpal, activeSearch matches, and this
    // result field is null in the stored snapshot. Otherwise push a new entry —
    // this covers both drill-downs (activeSearch changed) and re-searches of
    // the same kind (result already populated).
    //
    // A marker whose snapshot is FULLY empty (no queries, no results) is the
    // Mixpedia HOME — a real destination since the home-landing rework, so it
    // must be preserved (pushed over), never replaced. Without this, the
    // first search after entering Mixpedia swallowed the home stop and Back
    // fell through to whatever preceded it.
    const topLooksLikeHome =
        top && top.tab === 'djpal' && top.djpal &&
        top.djpal.artistResults == null &&
        top.djpal.trackResults == null &&
        top.djpal.mixResults == null &&
        top.djpal.trackListResults == null &&
        !top.djpal.searchAllQuery &&
        !top.djpal.artistQuery &&
        !top.djpal.trackQuery &&
        !top.djpal.mixQuery &&
        !top.djpal.trackListQuery;
    const topIsCleanMarker =
        top &&
        top.tab === 'djpal' &&
        !top.isHome &&
        !topLooksLikeHome &&
        top.djpal &&
        top.djpal.activeSearch === kind &&
        top.djpal[resultField] == null;

    if (topIsCleanMarker) {
        api.dispatch(replaceTopNavEntry({ ...top, djpal: snapshot }));
    } else {
        api.dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', djpal: snapshot }));
    }
};

navHistoryMiddleware.startListening({
    predicate: (action) => action.type === setArtistResults.type && action.payload != null,
    effect: handleResultsUpdate,
});

navHistoryMiddleware.startListening({
    predicate: (action) => action.type === setTrackResults.type && action.payload != null,
    effect: handleResultsUpdate,
});

navHistoryMiddleware.startListening({
    predicate: (action) => action.type === setMixResults.type && action.payload != null,
    effect: handleResultsUpdate,
});

navHistoryMiddleware.startListening({
    predicate: (action) => action.type === setTrackListResults.type && action.payload != null,
    effect: handleResultsUpdate,
});

// Closing a Mixpedia page (the × / clearResults) lands on the home — that's
// a real navigation destination, so push an entry for it. Without this,
// "artist → home → other artist → Back" skipped the home stop entirely.
navHistoryMiddleware.startListening({
    actionCreator: clearResults,
    effect: (action, api) => {
        if (isRealWeb) return;
        const { app, djpal } = api.getState();
        if (consumeSkip(app, api)) return;
        if (app.activeTab !== 'djpal') return;
        const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
        // Skip when the top entry is already an empty (home) djpal snapshot.
        const topIsHome =
            top && top.tab === 'djpal' && top.djpal &&
            top.djpal.artistResults == null &&
            top.djpal.trackResults == null &&
            top.djpal.mixResults == null &&
            top.djpal.trackListResults == null &&
            !top.djpal.searchAllQuery;
        if (topIsHome) return;
        // State is already cleared here, so the snapshot IS the home. The
        // isHome flag stops handleResultsUpdate from mistaking this entry for
        // a clean marker and REPLACING it with the next search (which made
        // Back skip the home stop).
        api.dispatch(pushNavEntry({ category: 'explore', tab: 'djpal', isHome: true, djpal: snapshotDjpal(djpal) }));
    },
});

// Expansion state isn't its own nav step, but the top entry's snapshot must
// reflect it — otherwise back-navigating into a multi-result list loses the
// expanded mix the user opened before drilling further.
navHistoryMiddleware.startListening({
    actionCreator: toggleExpandedMix,
    effect: (action, api) => {
        if (isRealWeb) return;
        const { app, djpal } = api.getState();
        if (consumeSkip(app, api)) return;
        if (app.activeTab !== 'djpal') return;
        const top = app.navIndex >= 0 ? app.navStack[app.navIndex] : null;
        if (!top || top.tab !== 'djpal') return;
        api.dispatch(replaceTopNavEntry({ ...top, djpal: snapshotDjpal(djpal) }));
    },
});

export default navHistoryMiddleware;
